import type {
  ApplicableRange,
  Enrollment,
  SessionBillingBreakdownResponse,
  SessionBillingSummary,
  SessionDetail,
  StudentBillingData,
  WeeklyBreakdown,
} from './get-session-billing-breakdown';
import {
  EnrollmentRosterStatus,
  HourlyTuitionPostingStatus,
  WeekChargeStatus,
} from './hourly-tuition.constants';

/** V2 preview payload (subset used by the mapper). */
export interface V2WeekCharge {
  weekStart: string;
  weekEnd: string;
  minutes: number;
  flatRate: number;
  amount: number;
  status: WeekChargeStatus;
  applicableRange?: ApplicableRange | null;
  postedInvoiceId?: string;
  /** Live SubscriptionInvoice.status — not WeekChargeStatus.POSTED. */
  postedInvoiceStatus?: string;
  combined?: {
    combinedMinutes: number;
    combinedFlatRate: number;
  };
}

export interface V2EnrollmentTuition {
  enrollmentId: string;
  enrollmentTitle: string;
  billingRuleName: string;
  weeks: V2WeekCharge[];
  billableTotal: number;
  billableMinutes: number;
  errorMessage: string | null;
}

export interface V2StudentTuition {
  studentId: string;
  studentName: string;
  parentId: string | null;
  enrollments: V2EnrollmentTuition[];
  grossTuition: number;
  multiStudentDiscount: number;
  staffDiscount: number;
  netTuition: number;
  postingStatus: HourlyTuitionPostingStatus;
  postedInvoiceId?: string;
  postedInvoiceStatus?: string;
}

export interface V2HourlyTuitionPreviewResult {
  success: boolean;
  data: V2StudentTuition[];
  summary: {
    totalStudents: number;
    totalEnrollments: number;
    totalBillingAmount: number;
    totalMinutes: number;
    dateRange: { fromDate: string; toDate: string };
  } | null;
  warnings?: Array<{ code: string; message: string }>;
  message?: string;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function isValidApplicableRange(
  range: ApplicableRange | null | undefined,
): range is ApplicableRange {
  if (!range) return false;
  return range.maxMinute > 0 || range.minMinute > 0;
}

function resolveWeekApplicableRange(week: V2WeekCharge): ApplicableRange | null {
  if (isValidApplicableRange(week.applicableRange)) {
    return week.applicableRange;
  }
  return null;
}

function minutesPerWeek(weeks: V2WeekCharge[]): number {
  const billable = weeks.filter(
    (week) => week.status === WeekChargeStatus.BILLABLE && week.minutes > 0,
  );
  if (billable.length > 0) return billable[0]!.minutes;
  const first = weeks.find((week) => week.minutes > 0);
  return first?.minutes ?? 0;
}

function buildSessionDetails(enrollment: V2EnrollmentTuition): SessionDetail[] {
  const perWeekMinutes = minutesPerWeek(enrollment.weeks);
  if (perWeekMinutes <= 0) {
    return [];
  }

  return [
    {
      day: 'Weekly',
      class: enrollment.enrollmentTitle,
      minutes: perWeekMinutes,
    },
  ];
}

function mapWeekCharge(week: V2WeekCharge): WeeklyBreakdown {
  const flatRate =
    week.combined?.combinedFlatRate && week.combined.combinedFlatRate > 0
      ? week.combined.combinedFlatRate
      : week.flatRate;

  return {
    weekStart: week.weekStart,
    weekEnd: week.weekEnd,
    minutes: week.minutes,
    hourlyRate: flatRate,
    billingAmount: week.amount,
    applicableRange: resolveWeekApplicableRange(week),
    errorMessage:
      week.status === WeekChargeStatus.OUT_OF_CHART
        ? 'Minutes outside rate chart'
        : null,
    isPosted: week.status === WeekChargeStatus.POSTED,
    postedInvoiceId: week.postedInvoiceId ?? null,
    // Never use WeekChargeStatus.POSTED here — that made Invoice Status show "Posted".
    postedInvoiceStatus: week.postedInvoiceStatus?.trim() || null,
    postedDateRange: null,
    isProrated:
      week.status === WeekChargeStatus.BILLABLE &&
      week.amount > 0 &&
      week.minutes > 0,
  };
}

function mapEnrollment(enrollment: V2EnrollmentTuition): Enrollment {
  const weeklyBreakdown = enrollment.weeks.map(mapWeekCharge);
  const representativeFlatRate =
    weeklyBreakdown.find((week) => week.hourlyRate > 0)?.hourlyRate ?? 0;
  const applicableRange =
    weeklyBreakdown.find((week) => week.applicableRange)?.applicableRange ??
    null;

  return {
    enrollmentId: enrollment.enrollmentId,
    enrollmentTitle: enrollment.enrollmentTitle,
    billingRuleName: enrollment.billingRuleName,
    status: EnrollmentRosterStatus.ACTIVE,
    sessionDetails: buildSessionDetails(enrollment),
    totalMinutes: enrollment.billableMinutes,
    hourlyRate: representativeFlatRate,
    totalTuition: enrollment.billableTotal,
    applicableRange,
    errorMessage: enrollment.errorMessage,
    weeklyBreakdown,
    // Discounts are student-level (MSD + staff). Do not invent 0 here —
    // review UI used to prefer this over the real group total and hide MSD.
    discountApplied: 0,
    finalAmount: enrollment.billableTotal,
  };
}

function mapStudent(student: V2StudentTuition): StudentBillingData {
  const multiStudentDiscount = roundMoney(student.multiStudentDiscount);
  const staffDiscount = roundMoney(student.staffDiscount);
  const discountApplied = roundMoney(multiStudentDiscount + staffDiscount);

  return {
    studentId: student.studentId,
    studentName: student.studentName,
    parentId: student.parentId ?? '',
    enrollments: student.enrollments.map(mapEnrollment),
    totalMinutes: student.enrollments.reduce(
      (sum, enrollment) => sum + enrollment.billableMinutes,
      0,
    ),
    totalTuition: student.grossTuition,
    // Total for display. Editable post payload must use discountValue only.
    discountApplied,
    multiStudentDiscount,
    // Staff/manual override only. Echoing multiStudent into this field made
    // post re-apply MSD as staff and double the discount ($10 -> $20).
    discountValue: staffDiscount,
    finalAmount: student.netTuition,
    postingStatus: student.postingStatus,
    postedInvoiceId: student.postedInvoiceId ?? null,
    postedInvoiceStatus: student.postedInvoiceStatus?.trim() || null,
    isDraft: false,
  };
}

function emptySummary(
  fromDate: string,
  toDate: string,
): SessionBillingSummary {
  return {
    totalStudents: 0,
    totalEnrollments: 0,
    totalBillingAmount: 0,
    totalMinutes: 0,
    dateRange: { fromDate, toDate },
  };
}

export function mapV2HourlyPreviewToSessionBillingBreakdown(
  preview: V2HourlyTuitionPreviewResult,
  fromDate: string,
  toDate: string,
): SessionBillingBreakdownResponse {
  const warningMessage =
    preview.warnings?.map((warning) => warning.message).join('\n') ||
    preview.message ||
    null;

  return {
    success: preview.success,
    data: preview.data.map(mapStudent),
    summary: preview.summary ?? emptySummary(fromDate, toDate),
    warningMessage,
    processingHistory: null,
  };
}
